/**
 * AuthorAssignmentMatrix — v3.18.8 (Strategy Phase 2)
 *
 * Per-section author + due-date grid. Joins the Proposal Architect
 * structure (volumes × sections) with persisted assignment rows so the
 * Capture Manager can see, in one place, who owns what and when each
 * section is due.
 *
 * Section catalog: GET /api/pursuits/:id/proposal-structure
 * Assignments:    GET/PUT/DELETE /api/pursuits/:id/strategy/author-assignments[/:section_id]
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, UserPlus, Save, X, Inbox } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'NOT_ASSIGNED', label: 'Not Assigned', tone: 'bg-slate-100 text-slate-700' },
  { value: 'ASSIGNED', label: 'Assigned', tone: 'bg-blue-100 text-blue-700' },
  { value: 'IN_PROGRESS', label: 'In Progress', tone: 'bg-amber-100 text-amber-700' },
  { value: 'IN_REVIEW', label: 'In Review', tone: 'bg-violet-100 text-violet-700' },
  { value: 'COMPLETE', label: 'Complete', tone: 'bg-emerald-100 text-emerald-700' },
];

const STATUS_BY_VALUE = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
}

function dueClass(iso, status) {
  if (!iso || status === 'COMPLETE') return 'text-slate-600';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const diff = (d - today) / 86400000;
  if (diff < 0) return 'text-rose-700 font-semibold';
  if (diff <= 3) return 'text-amber-700 font-semibold';
  return 'text-slate-700';
}

export default function AuthorAssignmentMatrix({ pursuitId }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState(null);
  const [assignments, setAssignments] = useState({}); // by section_id
  const [counts, setCounts] = useState({});
  const [editing, setEditing] = useState(null); // section_id currently editing
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!pursuitId) return;
    setLoading(true);
    try {
      const [structRes, asgnRes] = await Promise.all([
        fetch(`${API_URL}/api/pursuits/${pursuitId}/proposal-structure`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/pursuits/${pursuitId}/strategy/author-assignments`, { headers: getAuthHeaders() }),
      ]);
      if (structRes.ok) setStructure(await structRes.json());
      if (asgnRes.ok) {
        const body = await asgnRes.json();
        const map = {};
        for (const a of body.assignments || []) map[a.section_id] = a;
        setAssignments(map);
        setCounts(body.by_status || {});
      }
    } catch (e) {
      // silent — empty state will render
    } finally {
      setLoading(false);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Flatten the Architect structure into a list the matrix can render.
  const sections = useMemo(() => {
    if (!structure?.volumes) return [];
    const out = [];
    Object.entries(structure.volumes).forEach(([volNum, vol]) => {
      (vol.sections || []).forEach((s) => {
        out.push({
          section_id: s.section_id,
          section_label: s.section_title || s.title || s.section_id,
          volume_number: volNum,
          volume_title: vol.title || `Volume ${volNum}`,
        });
      });
    });
    return out;
  }, [structure]);

  const startEdit = (sec) => {
    const a = assignments[sec.section_id] || {};
    setEditing(sec.section_id);
    setDraft({
      assignee_name: a.assignee_name || '',
      assignee_email: a.assignee_email || '',
      due_date: a.due_date ? a.due_date.slice(0, 10) : '',
      status: a.status || 'ASSIGNED',
      notes: a.notes || '',
      section_label: sec.section_label,
      volume_number: sec.volume_number,
    });
  };

  const cancelEdit = () => { setEditing(null); setDraft({}); };

  const save = async (sectionId) => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/author-assignments/${encodeURIComponent(sectionId)}`,
        {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            section_label: draft.section_label,
            volume_number: draft.volume_number,
            assignee_name: draft.assignee_name?.trim() || null,
            assignee_email: draft.assignee_email?.trim() || null,
            due_date: draft.due_date || null,
            status: draft.status || 'ASSIGNED',
            notes: draft.notes || '',
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      toast.success('Assignment saved');
      cancelEdit();
      fetchAll();
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const clear = async (sectionId) => {
    if (!assignments[sectionId]) return;
    if (!confirm('Reset this section to unassigned?')) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/author-assignments/${encodeURIComponent(sectionId)}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error('Failed');
      toast.success('Assignment cleared');
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="py-10 text-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </CardContent></Card>
    );
  }

  if (sections.length === 0) {
    return (
      <Card data-testid="author-matrix-empty">
        <CardContent className="py-10 text-center text-slate-500">
          <Inbox className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No proposal sections yet. Run the Proposal Architect first — sections will populate here automatically.</p>
        </CardContent>
      </Card>
    );
  }

  const assignedCount = sections.filter((s) => assignments[s.section_id]).length;

  return (
    <Card data-testid="author-assignment-matrix">
      <CardContent className="p-0">
        {/* Summary bar */}
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2 bg-slate-50/60">
          <span className="text-xs text-slate-600 mr-2">
            <strong className="text-slate-900">{assignedCount}</strong> / {sections.length} sections assigned
          </span>
          {STATUS_OPTIONS.map((s) => (
            counts[s.value] ? (
              <Badge key={s.value} className={`${s.tone} hover:${s.tone}`} variant="secondary">
                {s.label}: {counts[s.value]}
              </Badge>
            ) : null
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="author-matrix-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 w-16">Vol</th>
                <th className="px-3 py-2">Section</th>
                <th className="px-3 py-2 w-56">Author</th>
                <th className="px-3 py-2 w-32">Due</th>
                <th className="px-3 py-2 w-32">Status</th>
                <th className="px-3 py-2 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sections.map((sec) => {
                const a = assignments[sec.section_id];
                const isEditing = editing === sec.section_id;
                const status = a?.status || 'NOT_ASSIGNED';
                const statusMeta = STATUS_BY_VALUE[status] || STATUS_BY_VALUE.NOT_ASSIGNED;

                if (isEditing) {
                  return (
                    <tr key={sec.section_id} className="bg-violet-50/40" data-testid={`author-matrix-row-${sec.section_id}`}>
                      <td className="px-3 py-2 align-top text-xs font-mono text-slate-600">{sec.volume_number}</td>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-slate-900">{sec.section_label}</div>
                        <div className="text-xs text-slate-500 font-mono">{sec.section_id}</div>
                        <Textarea
                          value={draft.notes || ''}
                          onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                          placeholder="Notes (optional)"
                          rows={2}
                          className="mt-2 text-xs"
                          data-testid={`author-notes-${sec.section_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top space-y-1">
                        <Input
                          value={draft.assignee_name || ''}
                          onChange={(e) => setDraft((d) => ({ ...d, assignee_name: e.target.value }))}
                          placeholder="Name"
                          className="h-8 text-xs"
                          data-testid={`author-name-${sec.section_id}`}
                        />
                        <Input
                          value={draft.assignee_email || ''}
                          onChange={(e) => setDraft((d) => ({ ...d, assignee_email: e.target.value }))}
                          placeholder="email@org.com"
                          type="email"
                          className="h-8 text-xs"
                          data-testid={`author-email-${sec.section_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Input
                          value={draft.due_date || ''}
                          onChange={(e) => setDraft((d) => ({ ...d, due_date: e.target.value }))}
                          type="date"
                          className="h-8 text-xs"
                          data-testid={`author-due-${sec.section_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <select
                          value={draft.status || 'ASSIGNED'}
                          onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                          className="h-8 text-xs border border-slate-200 rounded px-2 w-full bg-white"
                          data-testid={`author-status-${sec.section_id}`}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 align-top text-right space-x-1">
                        <Button
                          size="sm" variant="ghost" onClick={cancelEdit}
                          className="h-7 px-2 text-slate-600"
                          data-testid={`author-cancel-${sec.section_id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          disabled={saving}
                          onClick={() => save(sec.section_id)}
                          className="h-7 px-2 bg-violet-600 hover:bg-violet-700 text-white"
                          data-testid={`author-save-${sec.section_id}`}
                        >
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        </Button>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={sec.section_id}
                    className="hover:bg-slate-50"
                    data-testid={`author-matrix-row-${sec.section_id}`}
                  >
                    <td className="px-3 py-2 text-xs font-mono text-slate-600">{sec.volume_number}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{sec.section_label}</div>
                      <div className="text-xs text-slate-500 font-mono">{sec.section_id}</div>
                      {a?.notes ? (
                        <div className="text-xs text-slate-500 mt-1 italic line-clamp-1" title={a.notes}>
                          {a.notes}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      {a?.assignee_name || a?.assignee_email ? (
                        <div>
                          <div className="text-sm text-slate-900">{a.assignee_name || a.assignee_email}</div>
                          {a.assignee_email && a.assignee_name ? (
                            <div className="text-xs text-slate-500">{a.assignee_email}</div>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">—</span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-xs ${dueClass(a?.due_date, status)}`}>
                      {a?.due_date ? fmtDate(a.due_date) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded ${statusMeta.tone}`}>
                        {statusMeta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => startEdit(sec)}
                        className="h-7 px-2 text-violet-700 hover:bg-violet-50"
                        data-testid={`author-edit-${sec.section_id}`}
                      >
                        <UserPlus className="w-3.5 h-3.5 mr-1" />
                        {a ? 'Edit' : 'Assign'}
                      </Button>
                      {a ? (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => clear(sec.section_id)}
                          className="h-7 px-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50"
                          data-testid={`author-clear-${sec.section_id}`}
                        >
                          Clear
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
